import { useState, useEffect } from 'react';
import {
  X, ClockCounterClockwise, MagnifyingGlass, FileXls, FilePdf, Printer,
  CheckCircle, XCircle, Minus, Spinner,
} from '@phosphor-icons/react';
import { API } from './constants';

// V7 pos-piloto: relatorio de DISPONIBILIDADE do profissional
// Consolida:
//   Disponivel  = produziu OR disponivel
//   Ausente     = ausente
//   Sem registro = dias sem status registrado
const SITUACAO_DIA_CFG = {
  disponivel: { label: 'Disponivel', color: '#1B4332', bg: '#DCFCE7', Ic: CheckCircle },
  ausente: { label: 'Ausente', color: '#EF4444', bg: '#FEE2E2', Ic: XCircle },
  sem_registro: { label: 'Sem registro', color: '#A1A1AA', bg: '#F4F4F5', Ic: Minus },
};

const todayIso = () => new Date().toISOString().split('T')[0];
const addDays = (iso, delta) => {
  const d = new Date(iso + 'T12:00:00');
  d.setDate(d.getDate() + delta);
  return d.toISOString().split('T')[0];
};

export const HistoricoOperacionalDrawer = ({ profissionais, onClose }) => {
  // Default: ultimos 12 meses (para permitir validacao do historico existente)
  const [dataInicio, setDataInicio] = useState(addDays(todayIso(), -364));
  const [dataFim, setDataFim] = useState(todayIso());
  const [profissionalId, setProfissionalId] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const setPreset = (dias) => {
    setDataInicio(addDays(todayIso(), -(dias - 1)));
    setDataFim(todayIso());
  };

  const consultar = async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const params = new URLSearchParams({ data_inicio: dataInicio, data_fim: dataFim });
      if (profissionalId) params.set('profissional_id', profissionalId);
      const res = await fetch(`${API}/cod/historico?${params}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || 'Erro ao consultar historico');
      }
      const d = await res.json();
      setData(d);
    } catch (e) {
      setErrorMsg(e.message || 'Erro na consulta');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  // Auto-consultar ao abrir
  useEffect(() => { consultar(); }, []);

  const buildExportUrl = (kind) => {
    const params = new URLSearchParams({ data_inicio: dataInicio, data_fim: dataFim });
    if (profissionalId) params.set('profissional_id', profissionalId);
    return `${API}/cod/historico/export/${kind}?${params}`;
  };

  const handleImprimir = () => window.print();

  const temSemRegistro = data && data.totais.dias_sem_registro > 0;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="historico-drawer">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm print:hidden" onClick={onClose} />
      <div className="relative ml-auto h-full w-full max-w-4xl bg-[#F7F6F4] shadow-2xl overflow-y-auto print:max-w-full print:shadow-none">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-[#E4E4E7] px-6 py-4 flex items-center justify-between print:hidden">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#1B433215' }}>
              <ClockCounterClockwise size={20} weight="bold" style={{ color: '#1B4332' }} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>Historico Operacional</h2>
              <p className="text-xs text-[#71717A]">Visualize a operacao realizada por periodo.</p>
            </div>
          </div>
          <button data-testid="btn-close-historico" onClick={onClose}
            className="w-9 h-9 rounded-xl flex items-center justify-center text-[#71717A] hover:bg-[#F4F4F5]">
            <X size={20} weight="bold" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Filtros */}
          <div className="bg-white rounded-2xl border border-[#E4E4E7] p-5 print:hidden">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Data inicial</label>
                <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)}
                  data-testid="hist-data-inicio"
                  className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20" />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Data final</label>
                <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)}
                  data-testid="hist-data-fim"
                  className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20" />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Profissional</label>
                <select value={profissionalId} onChange={e => setProfissionalId(e.target.value)}
                  data-testid="hist-profissional"
                  className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20">
                  <option value="">Todos</option>
                  {(profissionais || []).map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
                </select>
              </div>
              <div className="flex items-end">
                <button onClick={consultar} disabled={loading}
                  data-testid="btn-consultar"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white transition-all hover:shadow-lg disabled:opacity-60"
                  style={{ backgroundColor: '#1B4332' }}>
                  {loading ? <Spinner size={16} className="animate-spin" /> : <MagnifyingGlass size={16} weight="bold" />}
                  {loading ? 'Consultando...' : 'Consultar'}
                </button>
              </div>
            </div>

            {/* Presets */}
            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-[#E4E4E7]">
              <span className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wide">Atalhos:</span>
              {[
                { label: 'Semana', d: 7 },
                { label: 'Quinzena', d: 15 },
                { label: 'Mes', d: 30 },
                { label: '90 dias', d: 90 },
                { label: '12 meses', d: 365 },
              ].map(p => (
                <button key={p.d} onClick={() => setPreset(p.d)}
                  data-testid={`hist-preset-${p.d}`}
                  className="px-3 py-1 rounded-full text-[11px] font-medium border border-[#E4E4E7] text-[#71717A] hover:bg-[#F7F6F4]">
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {errorMsg && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 print:hidden" data-testid="hist-error">
              {errorMsg}
            </div>
          )}

          {/* Totais gerais (Disponibilidade) */}
          {data && (
            <div className={`grid grid-cols-2 ${temSemRegistro ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-3`} data-testid="hist-totais">
              <StatCard label="Disponivel (dias)" value={data.totais.dias_disponivel} color="#1B4332" testid="tot-disponivel" />
              <StatCard label="Ausente (dias)" value={data.totais.dias_ausente} color="#EF4444" testid="tot-ausente" />
              {temSemRegistro && (
                <StatCard label="Sem registro (dias)" value={data.totais.dias_sem_registro} color="#A1A1AA" testid="tot-sem-registro" />
              )}
            </div>
          )}

          {/* Acoes exportacao */}
          {data && (
            <div className="flex flex-wrap items-center justify-between gap-2 print:hidden">
              <p className="text-xs text-[#71717A]">
                Periodo: <b>{data.periodo.data_inicio}</b> a <b>{data.periodo.data_fim}</b>
                {' · '}{data.periodo.dias_totais} dias
                {' · '}{data.resumo_por_profissional.length} profissional(is)
              </p>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={async () => {
                    const { downloadWithAuth } = await import('@/lib/apiClient');
                    await downloadWithAuth(buildExportUrl('excel'), `historico_cod_${dataInicio}_a_${dataFim}.xlsx`);
                  }}
                  data-testid="btn-export-excel"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-[#E4E4E7] text-[#71717A] hover:bg-white hover:text-[#18181B] transition-all"
                >
                  <FileXls size={16} weight="bold" /> Excel
                </button>
                <button type="button" onClick={async () => {
                    const { downloadWithAuth } = await import('@/lib/apiClient');
                    await downloadWithAuth(buildExportUrl('pdf'), `historico_cod_${dataInicio}_a_${dataFim}.pdf`);
                  }}
                  data-testid="btn-export-pdf"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-[#E4E4E7] text-[#71717A] hover:bg-white hover:text-[#18181B] transition-all"
                >
                  <FilePdf size={16} weight="bold" /> PDF
                </button>
                <button onClick={handleImprimir}
                  data-testid="btn-imprimir"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-[#E4E4E7] text-[#71717A] hover:bg-white hover:text-[#18181B] transition-all"
                >
                  <Printer size={16} weight="bold" /> Imprimir
                </button>
              </div>
            </div>
          )}

          {/* Resumo por Profissional */}
          {data && (
            <div className="bg-white rounded-2xl border border-[#E4E4E7] overflow-hidden">
              <div className="px-4 py-3 border-b border-[#E4E4E7]">
                <p className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wide">Resumo por Profissional</p>
              </div>
              {data.resumo_por_profissional.length === 0 ? (
                <div className="p-6 text-center text-sm text-[#71717A]" data-testid="hist-empty">
                  Nenhum registro de disponibilidade no periodo selecionado.
                </div>
              ) : (
                <div className="divide-y divide-[#F4F4F5]">
                  {data.resumo_por_profissional.map(r => (
                    <ProfRow
                      key={r.profissional_id}
                      row={r}
                      detalhe={data.detalhamento_diario[r.profissional_id] || []}
                      mostrarSemRegistro={temSemRegistro}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, color, testid }) => (
  <div className="bg-white rounded-2xl border border-[#E4E4E7] p-4" data-testid={testid}>
    <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color }}>{label}</p>
    <p className="text-2xl font-semibold mt-1 text-[#18181B]" style={{ fontFamily: 'Outfit' }}>{value}</p>
  </div>
);

const ProfRow = ({ row, detalhe, mostrarSemRegistro }) => {
  return (
    <div data-testid={`hist-prof-${row.profissional_id}`}>
      <div className="px-4 py-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[#18181B]" style={{ fontFamily: 'Outfit' }}>{row.profissional_nome}</p>
          <p className="text-[10px] text-[#A1A1AA] uppercase tracking-wide">{row.categoria || '-'}</p>
        </div>
        <div className="flex items-center gap-1.5">
          <Chip label="Disponivel" value={row.dias_disponivel} color="#1B4332" />
          <Chip label="Ausente" value={row.dias_ausente} color="#EF4444" />
          {mostrarSemRegistro && (
            <Chip label="S/ Registro" value={row.dias_sem_registro} color="#A1A1AA" />
          )}
        </div>
      </div>

      {detalhe.length > 0 && (
        <div className="bg-[#F7F6F4] px-4 py-3 border-t border-[#F4F4F5]" data-testid={`hist-detalhe-${row.profissional_id}`}>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {detalhe.map(d => <DayDetail key={d.data} d={d} />)}
          </div>
        </div>
      )}
    </div>
  );
};

const Chip = ({ label, value, color }) => (
  <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-[#F7F6F4] border border-[#E4E4E7]">
    <span className="text-[9px] font-semibold uppercase tracking-wide" style={{ color }}>{label}</span>
    <span className="text-xs font-semibold text-[#18181B]">{value}</span>
  </div>
);

const DayDetail = ({ d }) => {
  const cfg = SITUACAO_DIA_CFG[d.situacao] || SITUACAO_DIA_CFG.sem_registro;
  const Ic = cfg.Ic;
  return (
    <div className="bg-white rounded-lg border border-[#E4E4E7] px-2.5 py-1.5 flex items-center gap-2" style={{ backgroundColor: cfg.bg + '60' }}>
      <Ic size={14} weight="fill" style={{ color: cfg.color }} />
      <div className="min-w-0">
        <p className="text-[11px] font-medium text-[#18181B] leading-tight">{d.data}</p>
        <p className="text-[9px] font-semibold uppercase tracking-wide" style={{ color: cfg.color }}>{cfg.label}</p>
      </div>
    </div>
  );
};
